'use client'

import { CopySimpleIcon } from '@phosphor-icons/react'
import { useTranslations } from 'next-intl'

import { Copy } from '@/components/common/copy'
import { Image } from '@/components/common/image'
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerTitle,
  DrawerTrigger
} from '@/components/ui/drawer'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useModelConfig, useResolutionLabel } from '@/hooks'

import { useWorkContext } from './work-context'

export const WorkDetails = () => {
  const { data } = useWorkContext()
  const t = useTranslations()

  const model = useModelConfig(data?.extra.sceneId)

  const getResolutionLabel = useResolutionLabel(data?.extra.sceneId)

  const resolution = getResolutionLabel(data?.extra.resolution)

  if (!data) {
    return null
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex w-full items-center justify-between">
        <div>{t('work.prompt')}</div>
        <Copy asChild text={data?.extra.videoGenPrompt || ''} type="prompt">
          <button className="flex items-center gap-1">
            <CopySimpleIcon size={16} weight="bold" />
            <span className="text-xs font-medium">{t('common.copy')}</span>
          </button>
        </Copy>
      </div>
      <ScrollArea className="h-full">
        <div className="max-h-60 pr-4 text-sm break-words whitespace-pre-wrap">
          {data?.extra.videoGenPrompt}
        </div>
      </ScrollArea>
      <div className="mt-2 flex flex-col gap-4.5">
        <h2 className="text-sm font-bold">{t('work.generationData')}</h2>
        <ul className="flex flex-col gap-2">
          <li className="flex items-center justify-between">
            <span className="text-xs opacity-40">{t('work.model')}</span>
            <span className="rounded bg-light/10 px-1 py-0.5 text-xs font-medium">
              {model?.name}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span className="text-xs opacity-40">{t('work.orientation')}</span>
            <span className="rounded bg-light/10 px-1 py-0.5 text-xs font-medium">
              {data.extra.aspectRatio}
            </span>
          </li>
          <li className="flex items-center justify-between">
            <span className="text-xs opacity-40">{t('work.duration')}</span>
            <span className="rounded bg-light/10 px-1 py-0.5 text-xs font-medium">
              {data.extra.seconds}s
            </span>
          </li>
          {resolution && (
            <li className="flex items-center justify-between">
              <span className="text-xs opacity-40">{t('work.resolution')}</span>
              <span className="rounded bg-light/10 px-1 py-0.5 text-xs font-medium uppercase">
                {resolution}
              </span>
            </li>
          )}
          <li className="flex items-center gap-2">
            {data.extra.thumbnail && (
              <Image
                alt="start frame"
                className="size-12 rounded-lg object-cover"
                fill={false}
                height={48}
                src={data.extra.thumbnail}
                width={48}
              />
            )}
            {data.extra.lastImageUrl && (
              <Image
                alt="end frame"
                className="size-12 rounded-lg object-cover"
                fill={false}
                height={48}
                src={data.extra.lastImageUrl}
                width={48}
              />
            )}
          </li>
        </ul>
      </div>
    </div>
  )
}

export type WorkDetailsDrawerProps = {
  children: React.ReactNode
}

export const WorkDetailsDrawer = ({ children }: WorkDetailsDrawerProps) => {
  return (
    <Drawer>
      <DrawerTrigger asChild>{children}</DrawerTrigger>
      <DrawerContent className="z-modal px-4 pb-5">
        <DrawerTitle className="sr-only">Work Details</DrawerTitle>
        <DrawerDescription className="sr-only">Work Details</DrawerDescription>
        <WorkDetails />
      </DrawerContent>
    </Drawer>
  )
}
